/**
 * Blog Post Screen Component
 *
 * This component handles the rendering of individual blog posts using MDX.
 * It demonstrates:
 * - MDX bundling and rendering with custom components
 * - Frontmatter extraction for metadata
 * - Dynamic routing with React Router
 * - SEO optimization with meta tags
 * - Error handling for missing or invalid posts
 */
import type { Route } from "./+types/post";

import { readFile } from "node:fs/promises";
import path from "node:path";
import { data } from "react-router";

import {
  TypographyH1,
  TypographyH2,
  TypographyH3,
  TypographyH4,
  TypographyP,
} from "~/core/components/mdx-typography";
import { Badge } from "~/core/components/ui/badge";

/**
 * Meta function for the blog post page
 *
 * This function generates meta tags for SEO optimization and social sharing.
 * It handles two scenarios:
 * 1. When the post is found: Sets title, description, and Open Graph tags
 * 2. When the post is not found: Sets a 404 title
 *
 * The Open Graph tags enable rich previews when the post is shared on
 * social media platforms like Twitter, Facebook, and LinkedIn.
 *
 * @param data - The data returned from the loader function
 * @returns An array of meta tag objects for the page
 */
export const meta: Route.MetaFunction = ({ data }) => {
  // Handle case where post is not found
  if (!data) {
    return [
      {
        title: `404 Page Not Found | ${import.meta.env.VITE_APP_NAME}`,
      },
    ];
  }

  // Generate meta tags for found posts
  return [
    // Page title with post title and app name
    {
      title: `${data.frontmatter.title} | ${import.meta.env.VITE_APP_NAME}`,
    },
    // Meta description for search engines
    {
      name: "description",
      content: data.frontmatter.description,
    },
    // Open Graph image for social media previews
    {
      name: "og:image",
      content: `http://localhost:5173/api/blog/og?slug=${data.frontmatter.slug}`,
    },
    // Open Graph title for social media previews
    {
      name: "og:title",
      content: data.frontmatter.title,
    },
    // Open Graph description for social media previews
    {
      name: "og:description",
      content: data.frontmatter.description,
    },
  ];
};

/**
 * Server loader function for fetching and processing blog post content
 *
 * This function is responsible for:
 * 1. Finding the MDX file based on the URL slug parameter
 * 2. Bundling and processing the MDX content
 * 3. Extracting frontmatter metadata
 * 4. Handling errors for missing or invalid posts
 *
 * The MDX bundler compiles the markdown content into executable React components
 * while extracting the frontmatter metadata (title, date, author, etc.)
 *
 * @param params - Route parameters containing the post slug
 * @returns The processed MDX code and frontmatter metadata
 * @throws 404 error if the post is not found, 500 error for other issues
 */
export async function loader({ params }: Route.LoaderArgs) {
  // Construct the full path to the MDX file based on the slug parameter
  const filePath = path.join(
    process.cwd(),
    "app",
    "features",
    "blog",
    "docs",
    `${params.slug}.mdx`,
  );

  try {
    // Read MDX file content
    const content = await readFile(filePath, "utf-8");

    // Extract frontmatter using regex
    const frontmatterMatch = content.match(/^---\s*\n(.*?)\n---\s*\n/s);
    let frontmatter = {};
    let markdown = content;

    if (frontmatterMatch) {
      const frontmatterText = frontmatterMatch[1];
      markdown = content.slice(frontmatterMatch[0].length);

      // Parse simple frontmatter (key: value pairs)
      frontmatter = frontmatterText.split("\n").reduce((acc: any, line) => {
        const [key, ...valueParts] = line.split(":");
        if (key && valueParts.length > 0) {
          acc[key.trim()] = valueParts
            .join(":")
            .trim()
            .replace(/^["'](.*)["']$/, "$1");
        }
        return acc;
      }, {});
    }

    // Return frontmatter and markdown content
    return {
      frontmatter,
      markdown,
    };
  } catch (error) {
    // Handle file not found errors with a 404 response
    if (error instanceof Error && "code" in error && error.code === "ENOENT") {
      throw data(null, { status: 404 });
    }
    // Handle all other errors with a 500 response
    throw data(null, { status: 500 });
  }
}

/**
 * Blog Post Component
 *
 * This component renders a complete blog post with:
 * - Header with title, category, author, and date
 * - Featured image
 * - MDX content with custom styled typography components
 *
 * The MDX content is rendered using custom components for consistent styling
 * across all blog posts. This approach allows writing content in Markdown
 * while maintaining the design system's typography and styling.
 *
 * @param loaderData - Data from the loader containing frontmatter and compiled MDX code
 */
export default function Post({
  loaderData: { frontmatter, markdown },
}: Route.ComponentProps) {
  // Simple markdown rendering function
  const renderMarkdown = (content: string) => {
    const lines = content.split("\n");
    const elements: JSX.Element[] = [];

    lines.forEach((line, index) => {
      if (line.startsWith("# ")) {
        elements.push(<TypographyH1 key={index}>{line.slice(2)}</TypographyH1>);
      } else if (line.startsWith("## ")) {
        elements.push(<TypographyH2 key={index}>{line.slice(3)}</TypographyH2>);
      } else if (line.startsWith("### ")) {
        elements.push(<TypographyH3 key={index}>{line.slice(4)}</TypographyH3>);
      } else if (line.startsWith("#### ")) {
        elements.push(<TypographyH4 key={index}>{line.slice(5)}</TypographyH4>);
      } else if (line.trim()) {
        elements.push(<TypographyP key={index}>{line}</TypographyP>);
      }
    });

    return elements;
  };

  return (
    <div className="mx-auto w-full space-y-10">
      {/* Post header with category, title, author and date */}
      <header className="space-y-4">
        <div className="space-y-2">
          <Badge variant="secondary">{frontmatter.category}</Badge>
          <h1 className="text-3xl font-bold md:text-5xl lg:text-7xl">
            {frontmatter.title}
          </h1>
        </div>
        <span className="text-muted-foreground">
          {frontmatter.author} on{" "}
          {new Date(frontmatter.date).toLocaleDateString("ko-KR")}
        </span>
      </header>

      {/* Featured image for the post */}
      <img
        src={`/blog/${frontmatter.slug}.jpg`}
        alt={frontmatter.title}
        className="aspect-square w-full rounded-xl object-cover object-center"
      />

      {/* Render the markdown content with custom typography components */}
      <div className="space-y-4">{renderMarkdown(markdown)}</div>
    </div>
  );
}
